import dayJs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import titles from '@/assets/titles.json';
import api from '../api';

dayJs.extend(customParseFormat);

function createDefaultApplication() {
  return {
    Type: '',
    UserInfo: {},
    UnderstandingYou: {
      Member: {
        registrationNumber: '',
        qualifiedDate: '',
        sector: '',
        occupation: '',
        employmentStatus: '',
        jobTitle: '',
        isIndependentPrescriber: false,
        organization: '',
        isSupplementaryPrescriber: false,
        isPreRegTutor: false,
      },
      Associate: {
        previouslyRegistered: null,
        degreeType: '',
        otherQualification: '',
        degreeAwarded: '',
        university: '',
        alternativeUniversity: '',
        alternativeCountry: '',
        primarySector: '',
        primaryOccupation: '',
        employmentStatus: '',
        job: '',
        isIndependentPrescriber: false,
        isSupplementaryPrescriber: false,
        organisation: '',
        isPreRegTutor: false,
      },
      'Pharmaceutical Scientist': {
        sector: '',
        employmentStatus: '',
        jobTitle: '',
        area: '',
        degreesDoc: '',
        organization: '',
        cvDoc: '',
        cvFile: '',
        degreeFile: '',
      },
      'Foundation Trainee': {
        isRegistered: 'have never',
        university: '',
        universityOther: '',
        regNumber: '',
        assessment: '',
        studiedCountry: '',
        employmentStatus: 'Foundation trainee',
        organization: '',
        primarySector: '',
      },
      Student: {
        degreeType: '',
        degreeTypeOther: '',
        degreeStartYear: '',
        degreeEndYear: '',
        university: '',
        universityOther: '',
        country: '',
      },
    },
    PersonalDetails: {
      title: '',
      firstname: '',
      lastname: '',
      email: '',
      confirmEmail: '',
      password: '',
      confirmPassword: '',
      dob: '',
      tncs: false,
    },
    Correspondence: {
      postcode: null,
      addressLine1: null,
      addressLine2: null,
      addressLine3: null,
      town: null,
      country: null,
      ukCountry: null,
      phoneNumber: null,
      isOverseas: null,
    },
    Insurance: {
      optedOutOfPli: null,
      selfEmployedCoverForNonWorking: null,
      regulatoryProceedings: null,
      regulatoryProceedingsInfo: '',
      claimsInLastFiveYears: null,
      claimsInLastFiveYearsInfo: '',
      additionalRole: '',
    },
  };
}

function mapExistingToApplication(application, existing, userInfo) {
  const { Type, AuthorisationUrl, OpportunityId } = existing;
  const mapped = application;
  mapped.Type = Type;
  mapped.UserInfo = userInfo;
  mapped.AuthorisationUrl = AuthorisationUrl;
  mapped.OpportunityId = OpportunityId;
  // Map understanding you
  if (Type === 'Member') {
    mapped.UnderstandingYou.Member = {
      registrationNumber: existing.GPhCRegNo,
      qualifiedDate: dayJs(existing.QualificationDate).year().toString(),
      sector: existing.PrimarySector,
      occupation: existing.PrimaryOccupation,
      employmentStatus: existing.EmploymentStatus,
      jobTitle: existing.JobTitle,
      isIndependentPrescriber: existing.IndependentPrescriber,
      organization: existing.EmployerName,
      isSupplementaryPrescriber: existing.SupplementaryPrescriber,
      isPreRegTutor: existing.PreRegTutor,
    };
  } else if (Type === 'Associate') {
    mapped.UnderstandingYou.Associate = {
      previouslyRegistered: 'have never',
      degreeType: existing.DegreeAwardedMPharm,
      otherQualification: existing.DegreeAwardedOther,
      degreeAwarded: dayJs(existing.DegreeDateAwarded).format('DD/MM/YYYY'),
      university: existing.AwardingBodyOther ? 'Other' : existing.AwardingBody,
      alternativeUniversity: existing.AwardingBodyOther,
      alternativeCountry: existing.CountryQualifiedIn,
      primarySector: existing.PrimarySector,
      primaryOccupation: existing.PrimaryOccupation,
      employmentStatus: existing.EmploymentStatus,
      job: existing.JobTitle,
      isIndependentPrescriber: existing.IndependentPrescriber,
      organisation: existing.EmployerName,
      isSupplementaryPrescriber: existing.SupplementaryPrescriber,
      isPreRegTutor: existing.PreRegTutor,
    };
  } else if (Type === 'Student') {
    mapped.UnderstandingYou.Student = {
      degreeType: existing.DegreeAwardedMPharm,
      degreeTypeOther: existing.DegreeAwardedOther,
      degreeStartYear: dayJs(existing.DegreeDateStarted).year().toString(),
      degreeEndYear: dayJs(existing.DegreeDateWillFinish).year().toString(),
      university: existing.AwardingBodyOther ? 'Other' : existing.AwardingBody,
      universityOther: existing.AwardingBodyOther,
      country: existing.CountryQualifiedIn,
    };
  } else if (Type === 'Foundation Trainee') {
    mapped.UnderstandingYou['Foundation Trainee'] = {
      isRegistered: 'have never',
      university: existing.AwardingBodyOther ? 'Other' : existing.AwardingBody,
      universityOther: existing.AwardingBodyOther,
      regNumber: existing.GPhCPreRegNum,
      assessment: dayJs(existing.PreRegExamDate).format('YYYY-MM-DDTHH:mm:ss').toString(),
      studiedCountry: existing.CountryQualifiedIn,
      employmentStatus: 'Foundation trainee',
      primarySector: existing.PrimarySector,
      organization: existing.EmployerName,
    };
  } else if (Type === 'Pharmaceutical Scientist') {
    mapped.UnderstandingYou['Pharmaceutical Scientist'] = {
      sector: existing.PrimarySector,
      employmentStatus: existing.EmploymentStatus,
      jobTitle: existing.JobTitle,
      area: existing.AreaOfPharmaSci,
      organization: existing.EmployerName,
      degreesDoc: existing.DegreeCertificateURL,
      cvDoc: existing.CVURL,
    };
  }

  // Persona Details
  mapped.PersonalDetails = {
    title: existing.Title,
    firstname: existing.FirstName,
    lastname: existing.LastName,
    email: existing.Email,
    confirmEmail: existing.Email,
    password: null,
    confirmPassword: null,
    dob: dayJs(existing.BirthDate).format('DD/MM/YYYY'),
    tncs: false,
  };

  // Correspondence
  mapped.Correspondence = {
    postcode: existing.Postcode,
    addressLine1: existing.AddressLine1,
    addressLine2: existing.AddressLine2,
    addressLine3: existing.AddressLine3,
    town: existing.City,
    country: existing.Country,
    ukCountry: existing.UKCountry,
    phoneNumber: existing.PhoneNumber,
    isOverseas: existing.IsOverseas,
  };

  // PLI
  mapped.Insurance = {
    optedOutOfPli: existing.OptedOutOfPli,
    selfEmployedCoverForNonWorking: false,
    regulatoryProceedings: existing.RegulatoryProceedings,
    regulatoryProceedingsInfo: existing.RegulatoryProceedingsInfo,
    claimsInLastFiveYears: existing.ClaimsInLastFiveYears,
    claimsInLastFiveYearsInfo: existing.ClaimsInLastFiveYearsInfo,
    additionalRole: existing.AdditionalRole,
  };

  return mapped;
}

export default {
  async setOptions({ commit }) {
    const response = await api.understandingYouOptions();
    const options = response.data;

    commit('setOptions', options);
  },
  async getCurrentFees({ commit }) {
    try {
      const result = await api.getFees();
      const fees = result.data;
      commit('setPaymentOptions', fees);
      return { isSuccessful: true };
    } catch (e) {
      console.log(e);
      return {
        isSuccessful: false,
        message: 'There has been an issue retrieving the fees. Please contact the Membership Team on 0333 733 2570 so they can assist you.',
      };
    }
  },
  async saveApplicationToStore({ commit, state }, { application: existing, userInfo }) {
    let application = createDefaultApplication();

    application.Type = state.memberCategory;
    if (existing !== null) {
      application = mapExistingToApplication(application, existing, userInfo);
    } else if (userInfo.userId > 0) {
      application.UserInfo = userInfo;
      application.PersonalDetails.firstname = userInfo.firstname;
      application.PersonalDetails.lastname = userInfo.lastname;
      application.PersonalDetails.email = userInfo.email;
      application.PersonalDetails.confirmEmail = userInfo.email;
      application.PersonalDetails.dob = dayJs(userInfo.dob).format('DD/MM/YYYY');
      if (userInfo.title) {
        if (titles.some((t) => t.text === userInfo.title)) {
          application.PersonalDetails.title = userInfo.title;
        }
      }
    }
    commit('setApplication', application);
    commit('updateAreasWithOrWithoutInsuranceOption', application.Type);
  },
  saveUnderstandingYouToStore({ commit }, { understandingYou, selectedType: type }) {
    const update = understandingYou;

    if (type === 'Associate') {
      if (update.university !== 'Other') {
        update.alternativeUniversity = null;
      }
    }

    if (type === 'Foundation Trainee') {
      update.isRegistered = false;
      if (update.university !== 'Other') {
        update.universityOther = null;
        update.studiedCountry = 'United Kingdom';
      }
    }

    if (type === 'Student') {
      update.universityOther = update.university !== 'Other' ? null : update.universityOther;
      update.degreeTypeOther = update.degreeType === 'MPharm' ? null : update.degreeTypeOther;
    }

    commit('completeUnderstandingYou', {
      understandingYou: update,
      type,
    });
  },
  savePliRegulatoryDataToStore({ commit }, insurance) {
    const updateOfPliData = { ...insurance };

    updateOfPliData.regulatoryProceedings = insurance.regulatoryProceedings === 'Yes I have';
    if (!updateOfPliData.regulatoryProceedings) {
      updateOfPliData.regulatoryProceedingsInfo = '';
    }

    updateOfPliData.claimsInLastFiveYears = insurance.claimsInLastFiveYears === 'Yes I have';
    if (!updateOfPliData.claimsInLastFiveYears) {
      updateOfPliData.claimsInLastFiveYearsInfo = '';
    }

    commit('completeInsurance', {
      insurance: updateOfPliData,
    });
  },
  savePliOptOutData({ commit, getters }, insurance) {
    const updateOfPliData = { ...insurance };

    updateOfPliData.selfEmployedCoverForNonWorking = insurance.selfEmployedCoverForNonWorking === 'Self-employed';

    // set optedOut to true if overseas,retired or ft2025qualified or they set chose to opt out.
    updateOfPliData.optedOutOfPli = getters.ftQualifiedIn2025 || getters.getIsOverseas || getters.isRetiredMember
      || insurance.optedOutOfPli;

    if (updateOfPliData.optedOutOfPli) {
      updateOfPliData.selfEmployedCoverForNonWorking = false;
      updateOfPliData.additionalRole = 'Not Applicable';
    }

    commit('completeInsurance', {
      insurance: updateOfPliData,
    });
  },
};
